import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';
import { ensureListing } from '../../util/data';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {string?} props.conditionValue optional condition text for overlay badge
 * @returns {JSX.Element} listing image with fixed aspect ratio and optional condition overlay
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    conditionValue,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  return (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
      {conditionValue && (
        <div className={css.conditionOverlay}>
          {conditionValue}
        </div>
      )}
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', publicData, metadata = {} } = currentListing.attributes;
  const slug = createSlug(title);

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  // Helper to get field value with enum label resolution
  const getFieldDisplayValue = (fieldKey) => {
    if (!publicData) return null;
    
    const listingFields = config.listing?.listingFields || [];
    
    // Get value directly from publicData
    const rawValue = publicData[fieldKey];
    if (rawValue == null || rawValue === '' || rawValue === undefined) {
      // Also check metadata as fallback
      const metadataValue = metadata[fieldKey];
      if (metadataValue == null || metadataValue === '' || metadataValue === undefined) {
        return null;
      }
      const rawValueFromMetadata = metadataValue;
      
      // Try to find field config to get enum label
      const fieldConfig = listingFields.find(f => f.key === fieldKey);
      if (fieldConfig?.schemaType === 'enum' && fieldConfig.enumOptions) {
        const option = fieldConfig.enumOptions.find(opt => `${opt.option}` === `${rawValueFromMetadata}`);
        return option ? option.label : rawValueFromMetadata;
      }
      return rawValueFromMetadata;
    }
    
    // Try to find field config to get enum label
    const fieldConfig = listingFields.find(f => f.key === fieldKey);
    if (fieldConfig?.schemaType === 'enum' && fieldConfig.enumOptions) {
      const option = fieldConfig.enumOptions.find(opt => `${opt.option}` === `${rawValue}`);
      return option ? option.label : rawValue;
    }
    
    return rawValue;
  };
  
  // Get condition value for overlay
  const conditionValue = getFieldDisplayValue('condition');
  
  // Get size_group and size values for styled box
  const sizeGroupValue = getFieldDisplayValue('size_group');
  const sizeValue = getFieldDisplayValue('size');
  
  // Check if we have size fields to display
  const hasSizeFields = sizeGroupValue || sizeValue;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      {showListingImage ? (
        <ListingCardImage
          renderSizes={renderSizes}
          title={titlePlain}
          listing={listing}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          conditionValue={conditionValue}
        />
      ) : (
        <ListingCardThumbnail
          style={cardStyle}
          listingTitle={title}
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          setActivePropsMaybe={setActivePropsMaybe}
        />
      )}
      <div className={css.info}>
        <div className={css.mainInfo}>
          {showListingImage && <div className={css.title}>{titleFormatted}</div>}
          {hasSizeFields ? (
            <div className={css.customFieldsBox}>
              {sizeGroupValue && (
                <div className={css.sizeGroupBox}>
                  {sizeGroupValue}
                </div>
              )}
              {sizeValue && (
                <div className={css.sizeValueBox}>
                  Size: {sizeValue}
                </div>
              )}
            </div>
          ) : null}
        </div>
        <div className={css.priceAndAuthor}>
          {showPrice ? (
            <div className={css.price} title={priceTooltip}>
              {priceMessage}
            </div>
          ) : null}
          {showAuthorInfo ? (
            <div className={css.authorInfo}>{authorName}</div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
